import type {
  AccountSummary,
  AITask,
  AITaskResult,
  AIProviderSummary,
  ApiResponse,
  AvatarAgentStatus,
  CallRecord,
  Chat,
  ChatUpdateInput,
  Contact,
  ContactMemory,
  GlobalMemory,
  LinkedDevice,
  MediaRecord,
  MediaGenerationSummary,
  NvidiaTaskSummary,
  Message,
  VoiceSettings,
  VoiceConsent,
  VoiceSampleMeta,
  VoiceProfile,
  VoiceCallSession,
  WhatsAppTemplate,
  TemplateComponent,
  TemplateCategory,
  AdRecord,
  AdPlatform,
  AdPlatformSpec,
  SocialAccount,
  AdAutomation,
  SocialSettings,
  QuickReply,
  Reminder,
  ScheduledMessage,
  Settings,
  StyleProfile,
  SystemStatus,
  User,
  AtaMemoryResponse,
  AtaHealthReportsResponse,
  AtaSensitiveConsentResponse,
  AtaSensitiveCategory,
  AtaPredicateProposalsResponse,
  AtaTuningResponse,
  AtaTuningRollbackResponse,
} from '../types';

const configuredOrigin = import.meta.env.VITE_API_URL?.trim();
const resolvedOrigin = configuredOrigin
  ? new URL(configuredOrigin, window.location.href)
  : new URL(window.location.origin);
if (!['http:', 'https:'].includes(resolvedOrigin.protocol)) {
  throw new Error('VITE_API_URL must use HTTP or HTTPS');
}
export const apiOrigin =
  resolvedOrigin.origin === window.location.origin ? '' : resolvedOrigin.origin;

export class ApiClientError extends Error {
  public constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export type AiTaskError = {
  type: 'not_configured' | 'auth' | 'rate_limit' | 'timeout' | 'invalid' | 'network' | 'server';
  message: string;
  canRetry: boolean;
};

const AI_TASK_DUTCH_MESSAGES: Record<string, { type: AiTaskError['type']; message: string; canRetry: boolean }> = {
  AI_PROVIDER_NOT_CONFIGURED: { type: 'not_configured', message: 'AI-provider is niet geconfigureerd. Controleer de AI-provider instellingen.', canRetry: false },
  AI_PROVIDER_UNAVAILABLE: { type: 'not_configured', message: 'AI-provider is tijdelijk niet beschikbaar. Controleer of een provider actief is.', canRetry: false },
  AI_PROVIDER_AUTH_ERROR: { type: 'auth', message: 'AI-provider authenticatie mislukt. Controleer de API-sleutel.', canRetry: false },
  AI_PROVIDER_RATE_LIMIT: { type: 'rate_limit', message: 'AI-provider rate limit bereikt. Wacht even en probeer opnieuw.', canRetry: true },
  AI_PROVIDER_TIMEOUT: { type: 'timeout', message: 'AI-provider reageerde te laat. Probeer opnieuw.', canRetry: true },
  AI_PROVIDER_INVALID_RESPONSE: { type: 'invalid', message: 'AI-provider gaf een ongeldig antwoord. Probeer opnieuw.', canRetry: true },
  AI_PROVIDER_NETWORK_ERROR: { type: 'network', message: 'Verbinding met AI-provider mislukt. Controleer de netwerkverbinding.', canRetry: true },
  AI_PROVIDER_FAILURE: { type: 'server', message: 'AI-provider is tijdelijk niet beschikbaar. Probeer opnieuw.', canRetry: true },
};

export const classifyAiTaskError = (error: unknown): AiTaskError => {
  if (error instanceof ApiClientError) {
    const mapped = AI_TASK_DUTCH_MESSAGES[error.code];
    if (mapped) {
      const { type, message, canRetry } = mapped;
      return { type, message, canRetry };
    }
    if (error.status === 408 || error.status === 504) {
      return { type: 'timeout', message: 'AI-provider reageerde te laat. Probeer opnieuw.', canRetry: true };
    }
    if (error.status === 429) {
      return { type: 'rate_limit', message: 'AI-provider rate limit bereikt. Wacht even en probeer opnieuw.', canRetry: true };
    }
    if (error.status === 401 || error.status === 403) {
      return { type: 'auth', message: 'AI-provider authenticatie mislukt. Controleer de API-sleutel.', canRetry: false };
    }
    if (error.status >= 500) {
      return { type: 'server', message: 'AI-provider is tijdelijk niet beschikbaar. Probeer opnieuw.', canRetry: true };
    }
    return { type: 'server', message: 'AI-verzoek mislukt. Probeer later opnieuw.', canRetry: false };
  }
  if (error instanceof Error && error.name === 'AbortError') {
    return { type: 'timeout', message: 'Verzoek geannuleerd of verlopen. Probeer opnieuw.', canRetry: true };
  }
  if (error instanceof TypeError) {
    return { type: 'network', message: 'Verbinding mislukt. Controleer de netwerkverbinding.', canRetry: true };
  }
  return { type: 'server', message: 'AI-verzoek mislukt. Probeer later opnieuw.', canRetry: true };
};

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | undefined;
  body?: unknown;
  signal?: AbortSignal | undefined;
  retryCsrf?: boolean | undefined;
};

let csrfToken: string | null = null;

const readEnvelope = async <T>(response: Response): Promise<ApiResponse<T>> => {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    throw new ApiClientError(response.status, 'INVALID_RESPONSE', 'Server returned invalid data');
  }
  return (await response.json()) as ApiResponse<T>;
};

const fetchCsrf = async (): Promise<string> => {
  const response = await fetch(`${apiOrigin}/api/auth/csrf`, {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  const envelope = await readEnvelope<{ csrfToken: string }>(response);
  if (!response.ok || !envelope.success) {
    throw new ApiClientError(
      response.status,
      envelope.success ? 'CSRF_FAILED' : envelope.error.code,
      envelope.success ? 'Could not initialize request security' : envelope.error.message,
    );
  }
  csrfToken = envelope.data.csrfToken;
  return csrfToken;
};

const request = async <T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> => {
  const method = options.method ?? 'GET';
  const mutating = !['GET'].includes(method);
  const token = mutating ? (csrfToken ?? (await fetchCsrf())) : null;
  const headers = new Headers({ accept: 'application/json' });
  if (options.body !== undefined) headers.set('content-type', 'application/json');
  if (token) headers.set('x-csrf-token', token);

  const response = await fetch(`${apiOrigin}${path}`, {
    method,
    credentials: 'include',
    headers,
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
  const envelope = await readEnvelope<T>(response);
  if (
    !response.ok &&
    !envelope.success &&
    envelope.error.code === 'CSRF_INVALID' &&
    mutating &&
    options.retryCsrf !== false
  ) {
    csrfToken = null;
    await fetchCsrf();
    return request<T>(path, { ...options, retryCsrf: false });
  }
  if (!response.ok || !envelope.success) {
    const error = envelope.success
      ? { code: 'REQUEST_FAILED', message: 'Request failed', details: undefined }
      : envelope.error;
    const apiError = new ApiClientError(response.status, error.code, error.message, error.details);
    // Always log to console so hidden errors are visible to the developer.
    // 4xx (except 401/403/404/409) and all 5xx are surfaced as errors; benign
    // not-found and already-connected cases stay as warnings to reduce noise.
    const benign = response.status === 401 || response.status === 403 || response.status === 404 || response.status === 409;
    const tag = `[api ${method} ${path}]`;
    if (benign) {
      console.warn(tag, response.status, error.code, error.message);
    } else {
      console.error(tag, response.status, error.code, error.message, error.details ?? '');
    }
    throw apiError;
  }
  return envelope.data;
};

export const api = {
  bootstrap: (signal?: AbortSignal) =>
    request<{ setupRequired: boolean; registrationAllowed: boolean }>(
      '/api/auth/bootstrap',
      { signal },
    ),
  me: (signal?: AbortSignal) =>
    request<{ user: User; expiresAt: string }>('/api/auth/me', { signal }),
  login: async (input: { email: string; password: string }) => {
    const result = await request<{ user: User; csrfToken: string; expiresAt: string }>(
      '/api/auth/login',
      {
        method: 'POST',
        body: input,
      },
    );
    csrfToken = result.csrfToken;
    return result;
  },
  register: (input: { username: string; email: string; password: string }) =>
    request<{ user: User }>('/api/auth/register', { method: 'POST', body: input }),
  logout: async () => {
    await request<{ loggedOut: boolean }>('/api/auth/logout', { method: 'POST' });
    csrfToken = null;
  },
  startQrSession: () =>
    request<{ qrDataUrl: string; qrUrl: string; token: string; expiresAt: string }>(
      '/api/auth/qr-session/start',
      { method: 'POST' },
    ),
  getQrSessionStatus: (token: string, signal?: AbortSignal) =>
    request<{ status: 'pending' | 'consumed' | 'expired' }>(
      `/api/auth/qr-session/status/${encodeURIComponent(token)}`,
      { signal },
    ),
  consumeQrSession: async (token: string) => {
    const result = await request<{
      user: User;
      csrfToken: string;
      deviceId: string;
    }>('/api/auth/qr-session/consume', { method: 'POST', body: { token } });
    csrfToken = result.csrfToken;
    return result;
  },
  listDevices: (signal?: AbortSignal) =>
    request<{ devices: LinkedDevice[] }>('/api/auth/devices', { signal }),
  unlinkDevice: (deviceId: string) =>
    request<{ unlinked: boolean }>(
      `/api/auth/devices/${encodeURIComponent(deviceId)}/unlink`,
      { method: 'POST' },
    ),
  unlinkAllExcept: (deviceId: string) =>
    request<{ unlinked: boolean }>('/api/auth/devices/unlink-all-except', {
      method: 'POST',
      body: { deviceId },
    }),
  chats: (signal?: AbortSignal) => request<Chat[]>('/api/chats', { signal }),
  chat: (id: string, signal?: AbortSignal) =>
    request<Chat>(`/api/chats/${encodeURIComponent(id)}`, { signal }),
  createChat: (input: { title: string; phone?: string }) =>
    request<Chat>('/api/chats', { method: 'POST', body: input }),
  updateChat: (id: string, update: ChatUpdateInput) =>
    request<Chat>(`/api/chats/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: update,
    }),
  deleteChat: (id: string) =>
    request<{ deleted: true }>(`/api/chats/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  markChatRead: (id: string) =>
    request<Chat>(`/api/chats/${encodeURIComponent(id)}/read`, { method: 'POST' }),
  messages: (chatId: string, signal?: AbortSignal) =>
    request<Message[]>(`/api/messages/${encodeURIComponent(chatId)}`, { signal }),
  deleteMessage: (messageId: string) =>
    request<{ deleted: true }>(`/api/messages/${encodeURIComponent(messageId)}`, { method: 'DELETE' }),
  searchMessages: (
    chatId: string,
    options?: {
      search?: string;
      limit?: number;
      offset?: number;
      signal?: AbortSignal;
    },
  ) => {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.limit !== undefined) params.set('limit', String(options.limit));
    if (options?.offset !== undefined) params.set('offset', String(options.offset));
    const query = params.toString();
    return request<Message[]>(
      `/api/messages/${encodeURIComponent(chatId)}${query ? `?${query}` : ''}`,
      { signal: options?.signal },
    );
  },
  sendMessage: (chatId: string, input: { content: string; clientMessageId: string; replyToMessageId?: string | undefined }) =>
    request<Message>(`/api/messages/send`, {
      method: 'POST',
      body: { ...input, chatId },
    }),
  updateMessage: (messageId: string, update: { pinned: boolean }) =>
    request<Message>(`/api/messages/${encodeURIComponent(messageId)}`, {
      method: 'PATCH',
      body: update,
    }),
  reactToMessage: (messageId: string, emoji: string) =>
    request<Message>(`/api/messages/${encodeURIComponent(messageId)}/reactions`, {
      method: 'POST',
      body: { emoji },
    }),
  reportMessage: (messageId: string, reason?: string) =>
    request<{ reported: true }>(`/api/messages/${encodeURIComponent(messageId)}/report`, {
      method: 'POST',
      body: { reason },
    }),
  forwardMessage: (messageId: string, targetChatId: string) =>
    request<Message>(`/api/messages/${encodeURIComponent(messageId)}/forward`, {
      method: 'POST',
      body: { targetChatId },
    }),
  settings: (signal?: AbortSignal) =>
    request<Settings>('/api/settings', { signal }),
  updateSettings: (globalAutomode: boolean) =>
    request<Settings>('/api/settings', {
      method: 'PATCH',
      body: { globalAutomode },
    }),
  users: (signal?: AbortSignal) =>
    request<{ users: User[] }>('/api/user', { signal }),
  status: (signal?: AbortSignal) =>
    request<SystemStatus>('/api/status', { signal }),
  connectWhatsApp: () =>
    request<SystemStatus['whatsapp']>('/api/whatsapp/connect', { method: 'POST' }),
  disconnectWhatsApp: () =>
    request<SystemStatus['whatsapp']>('/api/whatsapp/disconnect', {
      method: 'POST',
    }),
  getQrCode: async (): Promise<{ qr: string }> =>
    request<{ qr: string }>('/api/whatsapp/qr', { method: 'GET' }),
  updateProfile: (update: {
    displayName?: string;
    avatarUrl?: string | null;
    styleProfile?: string | null;
  }) =>
    request<{ user: User }>('/api/user/profile', {
      method: 'PATCH',
      body: update,
    }),
  changePassword: async (input: {
    currentPassword: string;
    newPassword: string;
    newPasswordConfirm: string;
  }) => {
    const result = await request<{
      changed: boolean;
      user: User;
      csrfToken: string;
      expiresAt: string;
    }>('/api/auth/change-password', {
      method: 'POST',
      body: input,
    });
    csrfToken = result.csrfToken;
    return result;
  },
  refreshSession: async () => {
    const result = await request<{ refreshed: boolean; user: User; csrfToken: string; expiresAt: string }>(
      '/api/auth/refresh',
      {
        method: 'POST',
      },
    );
    csrfToken = result.csrfToken;
    return result;
  },
  contactsExport: async (accountId?: string): Promise<string> => {
    const params = accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
    const response = await fetch(`${apiOrigin}/api/contacts/export${params}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const envelope = await readEnvelope<unknown>(response);
      if (envelope.success) {
        throw new ApiClientError(response.status, 'REQUEST_FAILED', 'Request failed');
      }
      throw new ApiClientError(response.status, envelope.error.code, envelope.error.message);
    }
    return response.text();
  },
  contactsImport: (csv: string, accountId?: string) =>
    request<{ created: number; errors: string[] }>('/api/contacts/import', {
      method: 'POST',
      body: { csv, ...(accountId ? { accountId } : {}) },
    }),
  mediaUrl: (messageId: string, providerMediaId: string) =>
    `${apiOrigin}/api/whatsapp/media/${encodeURIComponent(messageId)}/${encodeURIComponent(providerMediaId)}`,
  mediaLocalUrl: (messageId: string) =>
    `${apiOrigin}/api/media/message/${encodeURIComponent(messageId)}`,
  accounts: (signal?: AbortSignal) =>
    request<AccountSummary[]>('/api/accounts', { signal }),
  createAccount: (input: {
    label: string;
    provider: 'whatsapp_cloud' | 'local_qr';
    sessionDir?: string;
    phoneNumberId?: string;
    businessAccountId?: string;
  }) => request<AccountSummary>('/api/accounts', { method: 'POST', body: input }),
  activateAccount: (accountId: string) =>
    request<{ activated: boolean }>(`/api/accounts/${encodeURIComponent(accountId)}/activate`, {
      method: 'POST',
    }),
  connectAccount: (accountId: string) =>
    request<AccountSummary['status']>(`/api/accounts/${encodeURIComponent(accountId)}/connect`, {
      method: 'POST',
    }),
  disconnectAccount: (accountId: string) =>
    request<AccountSummary['status']>(`/api/accounts/${encodeURIComponent(accountId)}/disconnect`, {
      method: 'POST',
    }),
  getAccountQr: (accountId: string) =>
    request<{ qr: string }>(`/api/accounts/${encodeURIComponent(accountId)}/qr`, {
      method: 'GET',
    }),
  removeAccount: (accountId: string) =>
    request<{ deleted: boolean }>(`/api/accounts/${encodeURIComponent(accountId)}`, {
      method: 'DELETE',
    }),
  contacts: (accountId?: string, signal?: AbortSignal) =>
    request<Contact[]>(
      `/api/contacts${accountId ? `?accountId=${encodeURIComponent(accountId)}` : ''}`,
      { signal },
    ),
  contact: (contactId: string, signal?: AbortSignal) =>
    request<Contact>(`/api/contacts/${encodeURIComponent(contactId)}`, { signal }),
  contactMemory: (contactId: string, signal?: AbortSignal) =>
    request<ContactMemory>(`/api/contacts/${encodeURIComponent(contactId)}/memory`, { signal }),
  createContact: (input: {
    accountId: string;
    phone: string;
    name: string;
    relationshipType?: Contact['relationshipType'];
    tags?: string[];
    notes?: string;
  }) => request<Contact>('/api/contacts', { method: 'POST', body: input }),
  updateContact: (contactId: string, update: Partial<Contact>) =>
    request<Contact>(`/api/contacts/${encodeURIComponent(contactId)}`, {
      method: 'PATCH',
      body: update,
    }),
  blockContact: (contactId: string) =>
    request<Contact>(`/api/contacts/${encodeURIComponent(contactId)}/block`, { method: 'POST' }),
  unblockContact: (contactId: string) =>
    request<Contact>(`/api/contacts/${encodeURIComponent(contactId)}/unblock`, { method: 'POST' }),
  activeCalls: (signal?: AbortSignal) =>
    request<CallRecord[]>('/api/calls/active', { signal }),
  callLog: (limit = 100, signal?: AbortSignal) =>
    request<CallRecord[]>(`/api/calls/log?limit=${limit}`, { signal }),
  acceptCall: (callId: string) =>
    request<CallRecord>(`/api/calls/${encodeURIComponent(callId)}/accept`, { method: 'POST' }),
  rejectCall: (callId: string) =>
    request<CallRecord>(`/api/calls/${encodeURIComponent(callId)}/reject`, { method: 'POST' }),
  endCall: (callId: string, summary?: string) =>
    request<CallRecord>(`/api/calls/${encodeURIComponent(callId)}/end`, {
      method: 'POST',
      body: summary ? { summary } : {},
    }),
  holdCall: (callId: string) =>
    request<CallRecord>(`/api/calls/${encodeURIComponent(callId)}/hold`, { method: 'POST' }),
  resumeCall: (callId: string) =>
    request<CallRecord>(`/api/calls/${encodeURIComponent(callId)}/resume`, { method: 'POST' }),
  transferCall: (callId: string, toOperatorId: string, reason?: string) =>
    request<CallRecord>(`/api/calls/${encodeURIComponent(callId)}/transfer`, {
      method: 'POST',
      body: { toOperatorId, ...(reason ? { reason } : {}) },
    }),
  outboundCall: (input: { accountId: string; phone: string; isVideo: boolean }) =>
    request<CallRecord>('/api/calls/outbound', { method: 'POST', body: input }),
  avatarStatus: (callId: string) =>
    request<AvatarAgentStatus>(`/api/calls/${encodeURIComponent(callId)}/avatar`, {
      method: 'GET',
    }),
  startAvatar: (callId: string) =>
    request<AvatarAgentStatus>(`/api/calls/${encodeURIComponent(callId)}/avatar/start`, {
      method: 'POST',
    }),
  stopAvatar: (callId: string, summary?: string) =>
    request<AvatarAgentStatus>(`/api/calls/${encodeURIComponent(callId)}/avatar/stop`, {
      method: 'POST',
      body: summary ? { summary } : {},
    }),
  avatarTakeover: (callId: string) =>
    request<{ takeover: boolean }>(`/api/calls/${encodeURIComponent(callId)}/avatar/takeover`, {
      method: 'POST',
    }),
  aiStatus: (signal?: AbortSignal) =>
    request<{ provider: string; state: string; configured: boolean }>(`/api/ai/status`, {
      signal,
    }),
  aiModels: (signal?: AbortSignal) =>
    request<{ models: Record<string, string>; provider: string; state: string }>(`/api/ai/models`, {
      signal,
    }),
  aiTask: (
    input: {
      task: AITask;
      messages: { role: 'user' | 'assistant'; content: string }[];
      contactId?: string;
      contactName?: string;
      modelOverride?: string;
    },
    options?: { signal?: AbortSignal },
  ) => request<AITaskResult>('/api/ai/task', { method: 'POST', body: input, signal: options?.signal }),
  analyzeMessages: (accountId: string, chatIds?: string[]) =>
    request<StyleProfile>('/api/ai/analyze-messages', {
      method: 'POST',
      body: { accountId, ...(chatIds ? { chatIds } : {}) },
    }),
  getAtaLearningStatus: (accountId: string, signal?: AbortSignal) =>
    request<{ analyzedCount: number; lastProcessedAt: string | null; pendingCount: number }>(
      `/api/ata/learning-status?accountId=${encodeURIComponent(accountId)}`,
      { signal },
    ),
  getStyle: (signal?: AbortSignal) =>
    request<StyleProfile>('/api/ai/style', { signal }),
  getStyleSuggestion: (signal?: AbortSignal) =>
    request<{ suggestion: string | null }>('/api/ai/style-suggestion', { signal }),
  updateStyle: (toneDescription: string) =>
    request<StyleProfile>('/api/ai/style', { method: 'PATCH', body: { toneDescription } }),
  getGlobalMemory: (signal?: AbortSignal) =>
    request<GlobalMemory>('/api/ai/global-memory', { signal }),
  getAtaMemory: (accountId: string) =>
    request<AtaMemoryResponse>(
      `/api/ata/memory?accountId=${encodeURIComponent(accountId)}`,
    ),
  invalidateAtaRelation: (relationId: string) =>
    request<{ success: boolean }>(
      `/api/ata/relations/${relationId}`,
      { method: 'DELETE' },
    ),
  getAtaHealthReports: (accountId: string) =>
    request<AtaHealthReportsResponse>(
      `/api/ata/health-reports?accountId=${encodeURIComponent(accountId)}`,
    ),
  getAtaSensitiveConsents: (accountId: string) =>
    request<AtaSensitiveConsentResponse>(
      `/api/ata/sensitive-consents?accountId=${encodeURIComponent(accountId)}`,
    ),
  updateAtaSensitiveDefault: (accountId: string, category: AtaSensitiveCategory, enabled: boolean | 'not_configured') =>
    request<{ success: boolean }>(
      `/api/ata/sensitive-defaults/${encodeURIComponent(accountId)}`,
      { method: 'PUT', body: { category, enabled } },
    ),
  getAtaPredicateProposals: (accountId: string) =>
    request<AtaPredicateProposalsResponse>(
      `/api/ata/predicate-proposals?accountId=${encodeURIComponent(accountId)}`,
    ),
  approveAtaPredicateProposal: (proposalId: string) =>
    request<{ success: boolean }>(
      `/api/ata/predicate-proposals/${proposalId}/approve`,
      { method: 'POST' },
    ),
  rejectAtaPredicateProposal: (proposalId: string) =>
    request<{ success: boolean }>(
      `/api/ata/predicate-proposals/${proposalId}/reject`,
      { method: 'POST' },
    ),
  getAtaTuning: (accountId: string) =>
    request<AtaTuningResponse>(
      `/api/ata/tuning?accountId=${encodeURIComponent(accountId)}`,
    ),
  rollbackAtaTuning: (logId: string) =>
    request<AtaTuningRollbackResponse>(
      `/api/ata/tuning/rollback/${logId}`,
      { method: 'POST' },
    ),
  bulkSend: (input: { chatIds: string[]; message: string; delay?: number }) =>
    request<{ results: { chatId: string; success: boolean; error?: string }[] }>(
      '/api/ai/bulk-send',
      { method: 'POST', body: input },
    ),
  sendMedia: (input: {
    chatId: string;
    clientMessageId: string;
    mediaType: 'image' | 'video' | 'audio' | 'document';
    mimeType: string;
    filename?: string;
    data: string;
    caption?: string;
  }) => request<Message>('/api/whatsapp/media', { method: 'POST', body: input }),
  features: (signal?: AbortSignal) =>
    request<Record<string, boolean>>('/api/features', { signal }),
  toggleFeature: (flag: string, enabled: boolean) =>
    request<Record<string, boolean>>(`/api/features/${encodeURIComponent(flag)}/toggle`, {
      method: 'POST',
      body: { enabled },
    }),
  callAnalytics: (days = 30, signal?: AbortSignal) =>
    request<{
      period: string;
      total: number;
      byStatus: Record<string, number>;
      voiceCalls: number;
      videoCalls: number;
      avgDurationSeconds: number;
      answerRate: number;
      byHour: { hour: number; calls: number }[];
      topContacts: { contactId: string; calls: number }[];
    }>(`/api/calls/analytics?days=${days}`, { signal }),
  followUpSuggestions: (chatId: string) =>
    request<{ suggestions: string[]; chatId: string; autoSend: boolean }>(
      '/api/ai/follow-up-suggestions',
      { method: 'POST', body: { chatId } },
    ),
  embeddedSignupStatus: (signal?: AbortSignal) =>
    request<{ enabled: boolean; configured: boolean; configIdPresent: boolean }>(
      '/api/whatsapp/embedded-signup/status',
      { signal },
    ),
  embeddedSignupStart: () =>
    request<{ redirectUrl: string; state: string }>(
      '/api/whatsapp/embedded-signup/start',
      { method: 'POST' },
    ),
  aiProviderStatus: (signal?: AbortSignal) =>
    request<{
      providers: AIProviderSummary[];
      active: AIProviderSummary | null;
      currentProvider: { provider: string; configured: boolean; state: string; reason?: string };
      availableProviders: { value: string; label: string; models: string[] }[];
    }>('/api/ai-provider/status', { signal }),
  saveAIProviderCredentials: (input: { provider: string; apiKey: string; model: string }) =>
    request<AIProviderSummary>('/api/ai-provider/credentials', { method: 'POST', body: input }),
  testAIProvider: (provider: string) =>
    request<{
      provider: string;
      success: boolean;
      latencyMs: number;
      errorCategory: string | null;
      errorCode: string | null;
      errorMessage: string | null;
    }>('/api/ai-provider/test', { method: 'POST', body: { provider } }),
  activateAIProvider: (provider: string) =>
    request<AIProviderSummary>('/api/ai-provider/activate', {
      method: 'POST',
      body: { provider },
    }),
  removeAIProviderCredentials: (provider: string) =>
    request<{ removed: boolean }>(
      `/api/ai-provider/credentials/${encodeURIComponent(provider)}`,
      { method: 'DELETE' },
    ),
  nvidiaTaskStatus: (signal?: AbortSignal) =>
    request<{
      slots: NvidiaTaskSummary[];
      availableSlots: {
        slot: 'learning' | 'ads';
        label: string;
        defaultModel: string;
        models: string[];
      }[];
    }>('/api/nvidia-task/status', { signal }),
  saveNvidiaTaskCredentials: (input: { slot: 'learning' | 'ads'; apiKey: string; model: string }) =>
    request<NvidiaTaskSummary>('/api/nvidia-task/credentials', { method: 'POST', body: input }),
  testNvidiaTask: (slot: 'learning' | 'ads') =>
    request<{
      slot: 'learning' | 'ads';
      success: boolean;
      latencyMs: number;
      errorCode: string | null;
      errorMessage: string | null;
    }>('/api/nvidia-task/test', { method: 'POST', body: { slot } }),
  removeNvidiaTaskCredentials: (slot: 'learning' | 'ads') =>
    request<{ removed: boolean }>(
      `/api/nvidia-task/credentials/${encodeURIComponent(slot)}`,
      { method: 'DELETE' },
    ),
  mediaGenerationStatus: (signal?: AbortSignal) =>
    request<{
      summary: MediaGenerationSummary | null;
      availableProviders: { value: string; label: string; models: string[] }[];
      defaultModel: Record<string, string>;
    }>('/api/media-generation/status', { signal }),
  saveMediaGenerationCredentials: (input: { provider: string; apiKey: string; model: string }) =>
    request<MediaGenerationSummary>('/api/media-generation/credentials', { method: 'POST', body: input }),
  testMediaGeneration: (provider: string) =>
    request<{
      success: boolean;
      latencyMs: number;
      errorCode: string | null;
      errorMessage: string | null;
    }>('/api/media-generation/test', { method: 'POST', body: { provider } }),
  removeMediaGenerationCredentials: (provider: string) =>
    request<{ removed: boolean }>(
      `/api/media-generation/credentials/${encodeURIComponent(provider)}`,
      { method: 'DELETE' },
    ),
  generateMedia: (input: { prompt: string; size?: string }) =>
    request<{ data: string; mimeType: string; filename: string }>('/api/media-generation/generate', {
      method: 'POST',
      body: input,
    }),
  quickReplies: (signal?: AbortSignal) =>
    request<QuickReply[]>('/api/quick-replies', { signal }),
  createQuickReply: (input: { shortcut: string; content: string }) =>
    request<QuickReply>('/api/quick-replies', { method: 'POST', body: input }),
  updateQuickReply: (id: string, input: { shortcut?: string; content?: string }) =>
    request<QuickReply>(`/api/quick-replies/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),
  deleteQuickReply: (id: string) =>
    request<{ deleted: boolean }>(`/api/quick-replies/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  scheduledMessages: (signal?: AbortSignal) =>
    request<ScheduledMessage[]>('/api/scheduled-messages', { signal }),
  createScheduledMessage: (input: { chatId: string; content: string; scheduledAt: string }) =>
    request<ScheduledMessage>('/api/scheduled-messages', { method: 'POST', body: input }),
  cancelScheduledMessage: (id: string) =>
    request<ScheduledMessage>(`/api/scheduled-messages/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  reminders: (signal?: AbortSignal) =>
    request<Reminder[]>('/api/reminders', { signal }),
  createReminder: (input: { chatId: string; title: string; remindAt: string }) =>
    request<Reminder>('/api/reminders', { method: 'POST', body: input }),
  cancelReminder: (id: string) =>
    request<Reminder>(`/api/reminders/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  mediaByChat: (chatId: string, signal?: AbortSignal) =>
    request<MediaRecord[]>(`/api/media/chat/${encodeURIComponent(chatId)}`, { signal }),
  mediaByIdUrl: (id: string) =>
    `${apiOrigin}/api/media/${encodeURIComponent(id)}`,
  // Voice
  voiceSettings: (signal?: AbortSignal) =>
    request<VoiceSettings>('/api/voice/settings', { signal }),
  updateVoiceSettings: (update: Partial<VoiceSettings>) =>
    request<VoiceSettings>('/api/voice/settings', { method: 'PATCH', body: update }),
  voiceScripts: (lang: string) =>
    request<{ language: string; phrases: string[] }>(`/api/voice/scripts/${encodeURIComponent(lang)}`),
  voiceConsent: (input: { version: string; retentionDays?: number; learningFromCall?: boolean }) =>
    request<VoiceConsent>('/api/voice/consent', { method: 'POST', body: input }),
  voiceUploadSample: (input: { phraseId: string; audioData: string; durationMs: number; sampleRate?: number }) =>
    request<VoiceSampleMeta>('/api/voice/samples', { method: 'POST', body: input }),
  voiceFinalizeEnrollment: (input: {
    consentId: string;
    samples: VoiceSampleMeta[];
    languagePrimary: string;
    displayName: string;
    provider: string;
    providerVoiceId: string;
  }) => request<VoiceProfile>('/api/voice/enrollment/finalize', { method: 'POST', body: input }),
  voiceProfile: (signal?: AbortSignal) =>
    request<VoiceProfile | null>('/api/voice/profile', { signal }),
  voiceDeleteProfile: (id: string) =>
    request<{ deleted: boolean }>(`/api/voice/profile/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  voiceCalls: (signal?: AbortSignal) =>
    request<VoiceCallSession[]>('/api/voice/calls', { signal }),
  // Templates
  templates: (signal?: AbortSignal) =>
    request<WhatsAppTemplate[]>('/api/templates', { signal }),
  createTemplate: (input: {
    name: string;
    category: TemplateCategory;
    language: string;
    components: TemplateComponent[];
  }) => request<WhatsAppTemplate>('/api/templates', { method: 'POST', body: input }),
  updateTemplate: (id: string, update: Partial<{
    name: string;
    category: TemplateCategory;
    language: string;
    components: TemplateComponent[];
    status: string;
    wabaTemplateId: string;
  }>) => request<WhatsAppTemplate>(`/api/templates/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: update,
  }),
  deleteTemplate: (id: string) =>
    request<{ deleted: boolean }>(`/api/templates/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  // Ads / Social Marketing
  ads: (signal?: AbortSignal) =>
    request<AdRecord[]>('/api/ads', { signal }),
  createAd: (input: Partial<AdRecord>) =>
    request<AdRecord>('/api/ads', { method: 'POST', body: input }),
  updateAd: (id: string, update: Partial<AdRecord>) =>
    request<AdRecord>(`/api/ads/${encodeURIComponent(id)}`, { method: 'PATCH', body: update }),
  deleteAd: (id: string) =>
    request<{ deleted: boolean }>(`/api/ads/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  publishAd: (id: string) =>
    request<AdRecord>(`/api/ads/${encodeURIComponent(id)}/publish`, { method: 'POST' }),
  scheduleAd: (id: string, scheduledAt: string) =>
    request<AdRecord>(`/api/ads/${encodeURIComponent(id)}/schedule`, { method: 'POST', body: { scheduledAt } }),
  adAnalytics: (id: string) =>
    request<{ clicks: number; conversions: number; ctr: number; clicksOverTime: { date: string; clicks: number }[] }>(
      `/api/ads/${encodeURIComponent(id)}/analytics`,
    ),
  adPlatforms: () =>
    request<Record<string, AdPlatformSpec>>('/api/ads/platforms'),
  adPublic: (id: string) =>
    request<AdRecord>(`/api/ads/public/${encodeURIComponent(id)}`),
  trackAdClick: (id: string, platform?: string) =>
    request<{ tracked: boolean }>(`/api/ads/${encodeURIComponent(id)}/click`, {
      method: 'POST',
      body: platform ? { platform } : {},
    }),
  socialAccounts: (signal?: AbortSignal) =>
    request<SocialAccount[]>('/api/social/accounts', { signal }),
  connectSocialAccount: (input: { platform: AdPlatform; accountName: string; accountHandle: string; credentials?: Record<string, string> }) =>
    request<SocialAccount>('/api/social/accounts', { method: 'POST', body: input }),
  disconnectSocialAccount: (id: string) =>
    request<{ deleted: boolean }>(`/api/social/accounts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  testSocialAccount: (id: string) =>
    request<{ success: boolean; message?: string }>(`/api/social/accounts/${encodeURIComponent(id)}/test`, { method: 'POST' }),
  socialSettings: (signal?: AbortSignal) =>
    request<SocialSettings>('/api/social/settings', { signal }),
  updateSocialSettings: (update: Partial<SocialSettings>) =>
    request<SocialSettings>('/api/social/settings', { method: 'PATCH', body: update }),
  adAutomations: (signal?: AbortSignal) =>
    request<AdAutomation[]>('/api/social/automations', { signal }),
  createAdAutomation: (input: Partial<AdAutomation>) =>
    request<AdAutomation>('/api/social/automations', { method: 'POST', body: input }),
  updateAdAutomation: (id: string, update: Partial<AdAutomation>) =>
    request<AdAutomation>(`/api/social/automations/${encodeURIComponent(id)}`, { method: 'PATCH', body: update }),
  deleteAdAutomation: (id: string) =>
    request<{ deleted: boolean }>(`/api/social/automations/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};